// src/pages/admin/AdminUsuarios.js
// Tabla con filtros de todos los usuarios (Identity + vínculo tenant + roles).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Table, TableHead, TableRow, TableCell,
  TableBody, TablePagination, Chip, IconButton, Stack, Alert, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, Divider, Grid, Snackbar,
} from '@mui/material';
import LockOpenIcon    from '@mui/icons-material/LockOpen';
import LockIcon        from '@mui/icons-material/Lock';
import MailIcon        from '@mui/icons-material/Mail';
import VisibilityIcon  from '@mui/icons-material/Visibility';
import KeyIcon         from '@mui/icons-material/Key';
import HistoryIcon     from '@mui/icons-material/History';
import SearchIcon      from '@mui/icons-material/Search';
import ClearIcon       from '@mui/icons-material/Clear';
import AddIcon         from '@mui/icons-material/Add';
import DeleteIcon      from '@mui/icons-material/Delete';
import { admin } from '../../api/authApi';
import TelefonoInput from '../../components/TelefonoInput';
import DialogCambiarPassword from '../../components/admin/DialogCambiarPassword';
import DialogHistorialAuditoria from '../../components/admin/DialogHistorialAuditoria';
import { fechaHoraLocal } from '../../utils/format';
import {
  useFiltrosUbicacion, SelectorDepartamento, SelectorMunicipio, SelectorGuia,
} from '../../components/admin/FiltrosUbicacion';

const ROLES = ['', 'SUPERADMIN', 'INVERSIONISTA', 'VALIDADOR', 'ADMIN_NEGOCIO', 'VECI', 'DOMICILIARIO'];
const ESTADOS = [
  { v: '', label: 'Todos' },
  { v: 'activo',        label: 'Activos' },
  { v: 'huerfano',      label: 'Huérfanos' },
  { v: 'sin_confirmar', label: 'Sin confirmar' },
  { v: 'bloqueado',     label: 'Bloqueados' },
];

const colorRol = (rol) => ({
  SUPERADMIN:     'error',
  INVERSIONISTA:  'secondary',
  VALIDADOR:      'success',
  ADMIN_NEGOCIO:  'primary',
  VECI:           'warning',
  DOMICILIARIO:   'info',
}[rol] || 'default');

export default function AdminUsuarios() {
  const [search, setSearch]   = useState('');
  const [rol, setRol]         = useState('');
  const [estado, setEstado]   = useState('');
  const [filtrosGeo, setFiltrosGeo] = useState({ departamentoId: '', municipioId: '', guiaId: '' });
  const [page, setPage]       = useState(0);
  const [size, setSize]       = useState(20);

  const [data, setData]       = useState({ total: 0, items: [] });
  const [cargando, setCargando] = useState(false);
  const [error, setError]     = useState('');
  const [password, setPassword] = useState(null);  // null | usuario
  const [historial, setHistorial] = useState(null); // null | usuario (D-27)
  const [aviso, setAviso]       = useState('');

  const [detalle, setDetalle] = useState(null);
  const [crear, setCrear]     = useState(null);   // { nombre, apellido, telefono, telIndicativo, telNumero, telValido, correo, password, rol }
  const [borrando, setBorrando] = useState(null);

  const geo = useFiltrosUbicacion({ cargarGuias: true });

  const limpiarFiltros = () => {
    setSearch('');
    setRol('');
    setEstado('');
    setFiltrosGeo({ departamentoId: '', municipioId: '', guiaId: '' });
    setPage(0);
  };
  const hayFiltros = !!(search || rol || estado
                        || filtrosGeo.departamentoId || filtrosGeo.municipioId || filtrosGeo.guiaId);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await admin.usuariosListar({
        search: search || undefined,
        rol:    rol    || undefined,
        estado: estado || undefined,
        departamentoId: filtrosGeo.departamentoId || undefined,
        municipioId:    filtrosGeo.municipioId    || undefined,
        guiaId:         filtrosGeo.guiaId         || undefined,
        page:   page + 1,
        size,
      });
      setData({ total: r.total, items: r.items });
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los usuarios.');
    } finally { setCargando(false); }
  }, [search, rol, estado, filtrosGeo, page, size]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDetalle = async (id) => {
    try { setDetalle(await admin.usuarioDetalle(id)); }
    catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar el detalle.'); }
  };

  const accion = async (fn, msg) => {
    try {
      await fn();
      if (detalle) setDetalle(await admin.usuarioDetalle(detalle.id));
      cargar();
    } catch (e) { alert(e.mensajeUsuario || msg); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Usuarios</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCrear({
            nombre: '', apellido: '',
            telIndicativo: '57', telNumero: '', telefono: '', telValido: false,
            correo: '', password: '', rol: 'INVERSIONISTA',
          })}
        >
          Nuevo usuario
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap"
                 useFlexGap sx={{ rowGap: 1.5 }}>
            <TextField
              label="Buscar (correo o nombre)"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              size="small"
              sx={{ flex: '1 1 260px', minWidth: 260 }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <TextField select label="Rol" value={rol}
              onChange={(e) => { setRol(e.target.value); setPage(0); }}
              size="small" sx={{ width: 170 }}>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r || 'Todos'}</MenuItem>)}
            </TextField>
            <TextField select label="Estado" value={estado}
              onChange={(e) => { setEstado(e.target.value); setPage(0); }}
              size="small" sx={{ width: 160 }}>
              {ESTADOS.map((e) => <MenuItem key={e.v} value={e.v}>{e.label}</MenuItem>)}
            </TextField>

            <SelectorDepartamento geo={geo} valor={filtrosGeo.departamentoId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, departamentoId: v, municipioId: '' })); setPage(0); }} />
            <SelectorMunicipio geo={geo} valor={filtrosGeo.municipioId}
              departamentoId={filtrosGeo.departamentoId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, municipioId: v })); setPage(0); }} />
            <SelectorGuia geo={geo} valor={filtrosGeo.guiaId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, guiaId: v })); setPage(0); }} />

            {hayFiltros && (
              <Button size="small" startIcon={<ClearIcon />} onClick={limpiarFiltros}
                      sx={{ ml: 'auto' }}>
                Limpiar
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Correo</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </TableCell></TableRow>
            )}
            {!cargando && data.items.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No hay usuarios que coincidan.
              </TableCell></TableRow>
            )}
            {!cargando && data.items.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.nombre || '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {u.roles.map((r) => <Chip key={r} size="small" label={r} color={colorRol(r)} />)}
                  </Stack>
                </TableCell>
                <TableCell>{u.entidadTipo || '—'}</TableCell>
                <TableCell>{u.tenantNombre || '—'}</TableCell>
                <TableCell>
                  {u.bloqueado       && <Chip size="small" label="Bloqueado" color="error" />}
                  {!u.emailConfirmado && <Chip size="small" label="Sin confirmar" color="warning" />}
                  {u.esHuerfano      && <Chip size="small" label="Huérfano" />}
                  {!u.bloqueado && u.emailConfirmado && !u.esHuerfano && (
                    <Chip size="small" label="Activo" color="success" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver detalle">
                    <IconButton size="small" onClick={() => abrirDetalle(u.id)}><VisibilityIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {u.bloqueado
                    ? <Tooltip title="Desbloquear"><IconButton size="small"
                          onClick={() => accion(() => admin.usuarioDesbloquear(u.id), 'No se pudo desbloquear.')}>
                          <LockOpenIcon fontSize="small" /></IconButton></Tooltip>
                    : <Tooltip title="Bloquear"><IconButton size="small"
                          onClick={() => accion(() => admin.usuarioBloquear(u.id), 'No se pudo bloquear.')}>
                          <LockIcon fontSize="small" /></IconButton></Tooltip>}
                  <Tooltip title="Cambiar contraseña">
                    <IconButton size="small" onClick={() => setPassword(u)}>
                      <KeyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* D-27: todo lo que se le ha hecho a esta cuenta. */}
                  <Tooltip title="Historial de auditoría">
                    <IconButton size="small" onClick={() => setHistorial(u)}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!u.emailConfirmado && (
                    <Tooltip title="Confirmar correo manualmente"><IconButton size="small"
                          onClick={() => accion(() => admin.usuarioConfirmarCorreo(u.id), 'No se pudo confirmar.')}>
                          <MailIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  <Tooltip title={u.esHuerfano ? 'Eliminar usuario' : 'Eliminar usuario (forzado)'}>
                    <IconButton size="small" color="error" onClick={() => setBorrando(u)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Card>

      {/* Modal de detalle */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth>
        {detalle && <>
          <DialogTitle>{detalle.nombre || detalle.email}</DialogTitle>
          <DialogContent dividers>
            <Field k="Correo" v={detalle.email} />
            <Field k="Teléfono" v={detalle.telefono || '—'} />
            <Field k="Documento" v={detalle.documento ? `${detalle.tipoDocumento || ''} ${detalle.documento}` : '—'} />
            <Field k="Correo confirmado" v={detalle.emailConfirmado ? 'Sí' : 'No'} />
            <Field k="Bloqueado" v={detalle.bloqueado ? `Sí (hasta ${fechaHoraLocal(detalle.bloqueadoHasta)})` : 'No'} />
            <Field k="Roles" v={detalle.roles.join(', ') || '—'} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Vínculo</Typography>
            {detalle.vinculo ? (
              <>
                <Field k="Tipo de entidad" v={detalle.vinculo.EntidadTipo || '—'} />
                <Field k="Id de entidad"   v={detalle.vinculo.EntidadID   ?? '—'} />
                <Field k="Rol tenant"      v={detalle.vinculo.Rol         || '—'} />
                <Field k="Tenant"          v={detalle.vinculo.tenantNombre || (detalle.vinculo.TenantID ?? '—')} />
                <Field k="Último acceso"   v={detalle.vinculo.UltimoAcceso ? fechaHoraLocal(detalle.vinculo.UltimoAcceso) : '—'} />
              </>
            ) : (
              <Alert severity="warning">Sin vínculo — usuario huérfano.</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetalle(null)}>Cerrar</Button>
          </DialogActions>
        </>}
      </Dialog>

      {/* Modal de crear usuario */}
      <Dialog open={!!crear} onClose={() => setCrear(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear usuario</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Por acá solo se crean usuarios <strong>SUPERADMIN</strong>, <strong>VALIDADOR</strong> o <strong>INVERSIONISTA</strong>.
            Los otros roles (veci, negocio, domiciliario) tienen su flujo de registro público con datos de negocio y aceptaciones legales.
          </Alert>
          {crear && (
            <Stack spacing={2}>
              <TextField
                select label="Rol" value={crear.rol}
                onChange={(e) => setCrear(s => ({ ...s, rol: e.target.value }))}
              >
                <MenuItem value="INVERSIONISTA">INVERSIONISTA — solo ve el proyecto</MenuItem>
                <MenuItem value="VALIDADOR">VALIDADOR — revisa documentos de negocios</MenuItem>
                <MenuItem value="SUPERADMIN">SUPERADMIN — control total</MenuItem>
              </TextField>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth label="Nombre(s)" value={crear.nombre}
                  onChange={(e) => setCrear(s => ({ ...s, nombre: e.target.value }))}
                />
                <TextField
                  fullWidth label="Apellido(s)" value={crear.apellido}
                  onChange={(e) => setCrear(s => ({ ...s, apellido: e.target.value }))}
                />
              </Stack>
              <TelefonoInput
                indicativo={crear.telIndicativo}
                numero={crear.telNumero}
                onChange={(ind, num, e164, valido) => setCrear(s => ({
                  ...s,
                  telIndicativo: ind, telNumero: num,
                  telefono: e164, telValido: valido,
                }))}
              />
              <TextField
                fullWidth type="email" label="Correo" value={crear.correo}
                onChange={(e) => setCrear(s => ({ ...s, correo: e.target.value }))}
                helperText="Se marca como confirmado automáticamente."
              />
              <TextField
                fullWidth type="text" label="Contraseña" value={crear.password}
                onChange={(e) => setCrear(s => ({ ...s, password: e.target.value }))}
                helperText="Mínimo 6 caracteres. Se la pasas al usuario por WhatsApp o correo."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrear(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!crear?.nombre || !crear?.apellido || !crear?.telValido || !crear?.correo?.includes('@') || (crear?.password || '').length < 6}
            onClick={async () => {
              try {
                await admin.usuarioCrear({
                  nombre:   crear.nombre.trim(),
                  apellido: crear.apellido.trim(),
                  telefono: crear.telefono,
                  correo:   crear.correo.trim(),
                  password: crear.password,
                  rol:      crear.rol,
                });
                setCrear(null);
                cargar();
              } catch (e) {
                alert(e.mensajeUsuario || 'No se pudo crear el usuario.');
              }
            }}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de eliminar */}
      <Dialog open={!!borrando} onClose={() => setBorrando(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar usuario</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            ¿Seguro que quieres eliminar a <strong>{borrando?.email}</strong>?
          </Typography>
          {borrando && !borrando.esHuerfano && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Este usuario tiene vínculo activo con {borrando.tenantEntidadTipo || 'un tenant'}
              {borrando.tenantNombre ? ` (${borrando.tenantNombre})` : ''}.
              Se revocará el vínculo y se eliminará. Las entidades operacionales
              (pedidos, fiados, catálogo) NO se borran: quedan sin usuario que las opere.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Esta acción libera el correo y el teléfono para poder crear otro usuario con los mismos datos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBorrando(null)}>Cancelar</Button>
          <Button
            color="error" variant="contained"
            onClick={async () => {
              try {
                const forzar = !borrando.esHuerfano;
                const r = await admin.usuarioEliminar(borrando.id, forzar);
                setBorrando(null);
                cargar();
                if (r?.vinculosRevocados > 0) {
                  // notificación simple via alert; si tienes snackbar, cámbialo.
                  alert(`Usuario eliminado. Se revocaron ${r.vinculosRevocados} vínculo(s) activo(s).`);
                }
              } catch (e) {
                alert(e.mensajeUsuario || 'No se pudo eliminar.');
              }
            }}
          >
            {borrando && !borrando.esHuerfano ? 'Eliminar de todos modos' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogCambiarPassword
        abierto={password !== null}
        aspNetUserId={password?.id}
        nombre={password?.nombre || password?.email}
        detalle={[password?.email, password?.telefono].filter(Boolean).join(' · ')}
        onCerrar={() => setPassword(null)}
        onCambiada={setAviso}
      />

      <DialogHistorialAuditoria
        abierto={historial !== null}
        entidad="Usuario"
        entidadId={historial?.id}
        titulo={historial?.nombre || historial?.email}
        onCerrar={() => setHistorial(null)}
      />

      <Snackbar
        open={!!aviso} autoHideDuration={6000} onClose={() => setAviso('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setAviso('')}>{aviso}</Alert>
      </Snackbar>
    </Box>
  );
}

const Field = ({ k, v }) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{k}</Typography>
    <Typography variant="body2">{v}</Typography>
  </Stack>
);
